import { Container } from "@mui/material";
import React, { useEffect, useState, useContext } from "react";

import {
  Burger,
  Button,
  Group,
  ActionIcon,
  Tooltip,
  Drawer,
  Stack,
  Text,
  Avatar,
  Menu,
} from "@mantine/core";
import { useDisclosure, useWindowScroll, useMediaQuery } from "@mantine/hooks";
import { IconSun, IconMoon, IconLogin } from "@tabler/icons-react";
import { IconSearch } from "@tabler/icons-react";
import { IconX } from "@tabler/icons-react";
import { IconMenu2 } from "@tabler/icons-react";

import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Colors } from "../../Lib/data";
import { AllFatherContext } from "../../App";
import "./styles.scss";
import { RouteList } from "../../Lib/routes";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../Lib/i18n/LanguageSelector";
import { useMantineColorScheme } from "@mantine/core";
import { AppContext } from "../../Pages/Dashboard";

const TopNav: React.FC = () => {
  const navigate = useNavigate();
  const allFatherContext = useContext(AllFatherContext);
  const authContext = useContext(AppContext);
  const [opened, { toggle }] = useDisclosure(false);
  const [active, setActive] = useState(RouteList[0].link);
  const [scroll] = useWindowScroll();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 1100px)");

  const isStudentLoggedIn =
    authContext && authContext.profile && authContext?.profile?.id?.length > 0 ? true : false;

  const { theme, toggleTheme } = allFatherContext || {
    theme: "light",
    toggleTheme: () => {},
  };

  const handleLogout = () => {
    // TODO: Implement logout functionality
    localStorage.removeItem("t");

    navigate("/login");
  };

  useEffect(() => {
    setActive(location.pathname);
  }, [location.pathname]);

  const items = RouteList.map((link) => (
    <Link to={link.link} key={link.label}>
      <Button
        className={"link"}
        size="xs"
        fz={14}
        c={active !== link.link ? Colors.PurplePrimary : Colors.White}
        color={Colors.PurplePrimary}
        variant={active === link.link ? "outline" : "subtle"}
        onClick={() => {
          setActive(link.link);
        }}
      >
        {t(`navigation.${link.label}`)}
      </Button>
    </Link>
  ));

  const { colorScheme } = useMantineColorScheme();

  if (isMobile) {
    // Minimal, theme-aware mobile nav with Menu and theme toggler
    return (
      <nav className="topnav-mobile-theme">
        <Group
          justify="space-between"
          align="center"
          px={12}
          py={0}
          style={{ minHeight: 48, height: 48 }}
        >
          {/* Logo */}
          <Avatar src={allFatherContext?.assets?.logos?.legacy} size={32} radius={8} alt="Logo" />
          <Group gap={2}>
            {/* Theme toggler */}
            <ActionIcon
              variant="transparent"
              size={36}
              aria-label="Toggle theme"
              onClick={toggleTheme}
            >
              {theme === "dark" ? <IconSun size={20} /> : <IconMoon size={20} />}
            </ActionIcon>
            {/* Search icon for mobile */}
            <ActionIcon
              variant="subtle"
              size={36}
              aria-label="Search"
              onClick={() => navigate("/search")}
            >
              <IconSearch size={20} />
            </ActionIcon>

            {/* Language selector */}
            <LanguageSelector scrollY={0} theme={theme} />
            {/* Burger/Menu */}
            <Menu
              shadow="md"
              width={200}
              position="bottom-end"
              withArrow
              transitionProps={{ transition: "pop", duration: 120 }}
              classNames={{ dropdown: "topnav-mobile-menu" }}
            >
              <Menu.Target>
                <ActionIcon variant="subtle" size={36} aria-label="Open menu">
                  <IconMenu2 size={22} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {RouteList.map((link) => (
                  <Menu.Item
                    key={link.label}
                    leftSection={<link.icon size={16} style={{ marginRight: 8 }} />}
                    onClick={() => navigate(link.link)}
                    color={active === link.link ? "violet" : undefined}
                    style={{
                      fontWeight: active === link.link ? 700 : 500,
                      background:
                        active === link.link
                          ? theme === "dark"
                            ? Colors.PurplePrimary
                            : Colors.PurplePrimary + "11"
                          : undefined,
                      color: active === link.link ? Colors.White : undefined,
                    }}
                  >
                    {t(`navigation.${link.label}`)}
                  </Menu.Item>
                ))}
                {/* Add logout/login option */}
                <Menu.Divider />
                {isStudentLoggedIn ? (
                  <Menu.Item
                    color="red"
                    onClick={handleLogout}
                    leftSection={<IconX size={16} style={{ marginRight: 8 }} />}
                    style={{ fontWeight: 700 }}
                  >
                    {t("navigation.logout")}
                  </Menu.Item>
                ) : (
                  <Menu.Item
                    color="violet"
                    onClick={() => navigate("/login")}
                    leftSection={<IconLogin size={16} style={{ marginRight: 8 }} />}
                    style={{ fontWeight: 700 }}
                  >
                    {t("auth.signIn")}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </nav>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        backgroundColor: theme === "dark" ? "var(--bg-primary)" : Colors.White,
      }}
      transition={{ duration: 0.28 }}
      className="top-nav-container"
      style={{
        borderBottom: theme === "dark" ? "1px solid var(--border-color)" : "none",
      }}
    >
      <Container maxWidth="lg" className="content">
        <header className={"header flex-row align-center justify-between"}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Link to="/">
              <img src={allFatherContext?.assets?.logos?.legacy} alt="logo" className="logo" />
            </Link>
            <ActionIcon
              variant="subtle"
              size="lg"
              style={{ marginLeft: 8 }}
              onClick={() => navigate("/search")}
              aria-label={t("common.search")}
            >
              <IconSearch size={22} />
            </ActionIcon>
          </div>
          <div className="flex-row align-center justify-between">
            <Group gap={5} visibleFrom="xs">
              {items}

              {/* Dark Mode Toggle */}
              <Tooltip
                label={theme === "dark" ? t("theme.lightMode") : t("theme.darkMode")}
                position="bottom"
                withArrow
              >
                <ActionIcon
                  onClick={toggleTheme}
                  className="theme-toggle"
                  variant="subtle"
                  size="lg"
                  radius="md"
                  color={Colors.PurplePrimary}
                  styles={{
                    root: {
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(98, 0, 238, 0.1)",
                        transform: "scale(1.1)",
                      },
                    },
                  }}
                >
                  <motion.div
                    key={theme}
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                      type: "spring",
                      stiffness: 200,
                      damping: 10,
                    }}
                  >
                    {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
                  </motion.div>
                </ActionIcon>
              </Tooltip>

              {/* Language Selector */}
              <LanguageSelector scrollY={scroll.y} theme={theme} />

              {isStudentLoggedIn ? (
                <Button
                  className="logout-button"
                  size="xs"
                  fz={14}
                  c={Colors.PurplePrimary}
                  color={Colors.PurplePrimary}
                  variant="subtle"
                  onClick={handleLogout}
                  styles={{
                    root: {
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(98, 0, 238, 0.1)",
                      },
                    },
                  }}
                >
                  {t("navigation.logout")}
                </Button>
              ) : (
                <Button
                  className="login-button"
                  size="xs"
                  fz={14}
                  c={Colors.White}
                  color={Colors.PurplePrimary}
                  variant="filled"
                  leftSection={<IconLogin size={16} style={{ marginRight: 8 }} />}
                  onClick={() => navigate("/login")}
                  styles={{
                    root: {
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: Colors.PurplePrimary,
                        transform: "translateY(-1px)",
                      },
                    },
                  }}
                >
                  {t("auth.signIn")}
                </Button>
              )}
            </Group>

            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="xs"
              size="sm"
              color={Colors.PurplePrimary}
            />
          </div>
        </header>
      </Container>

      {/* Mobile Navigation Drawer */}
      <Drawer
        opened={opened}
        onClose={toggle}
        size="100%"
        padding="xl"
        title={
          <Group justify="space-between" align="center" w="100%">
            <Link to="/">
              <img
                src={allFatherContext?.assets?.logos?.legacy}
                alt="logo"
                style={{ width: 40, height: 40 }}
              />
            </Link>
            <ActionIcon variant="subtle" size="lg" onClick={toggle}>
              <IconX size={24} />
            </ActionIcon>
          </Group>
        }
        styles={{
          header: {
            borderBottom: "1px solid var(--mantine-color-gray-3)",
            paddingBottom: "1rem",
          },
        }}
      >
        <Stack gap="lg" mt="xl">
          {/* Main Navigation Links */}
          {RouteList.map((link) => (
            <Link to={link.link} key={link.label} onClick={toggle}>
              <Button
                fullWidth
                variant={active === link.link ? "filled" : "subtle"}
                size="lg"
                justify="flex-start"
                leftSection={<link.icon size={20} />}
                styles={{
                  root: {
                    textAlign: "left",
                    padding: "1rem",
                    borderRadius: "12px",
                  },
                }}
              >
                {t(`navigation.${link.label}`)}
              </Button>
            </Link>
          ))}

          {/* Divider */}
          <div
            style={{
              height: "1px",
              background: "var(--mantine-color-gray-3)",
              margin: "1rem 0",
            }}
          />

          {/* Language Selector */}
          <Group justify="center">
            <LanguageSelector scrollY={scroll.y} theme={theme} />
          </Group>

          {/* Logout/Login Button */}
          {isStudentLoggedIn ? (
            <Button
              fullWidth
              variant="subtle"
              color="red"
              size="lg"
              onClick={() => {
                toggle();
                handleLogout();
              }}
              styles={{
                root: {
                  border: "1px solid var(--mantine-color-red-3)",
                  borderRadius: "12px",
                },
              }}
            >
              {t("navigation.logout")}
            </Button>
          ) : (
            <Button
              fullWidth
              variant="filled"
              color="violet"
              size="lg"
              leftSection={<IconLogin size={16} style={{ marginRight: 8 }} />}
              onClick={() => {
                toggle();
                navigate("/login");
              }}
              styles={{
                root: {
                  borderRadius: "12px",
                },
              }}
            >
              {t("auth.signIn")}
            </Button>
          )}
        </Stack>
      </Drawer>
    </motion.div>
  );
};

export default TopNav;
